const addProductPrefix = (sku) =>
  typeof sku === 'string' && !sku.startsWith('product:')
    ? `product:${sku}`
    : sku

class ReportPgAdapter {
  constructor (pgConnection) {
    this.pgConnection = pgConnection
  }

  // Returns stock count reports for a location+program, latest first.
  // Each report includes per-generic-sku soh and consumed values.
  async list ({ locationId, programId, limit = 10 }) {
    if (!locationId) throw new Error('locationId is required')
    if (!programId) throw new Error('programId is required')

    const { rows } = await this.pgConnection.query(`
      WITH latest_counts AS (
        SELECT id, stock_count_id, fs_id, program_id, yearweek, submitted_at
        FROM avocado.data_stockcount
        WHERE fs_id = $1 AND program_id = $2
        ORDER BY submitted_at DESC
        LIMIT $3
      )
      SELECT
        lc.id,
        lc.stock_count_id,
        lc.fs_id AS location_id,
        lc.program_id,
        lc.yearweek,
        lc.submitted_at,
        scl.sku,
        scl.closing,
        scl.sold,
        scl.extra_fields
      FROM latest_counts lc
      LEFT JOIN avocado.data_stockcountline scl ON scl.stock_count_id = lc.id
      ORDER BY lc.submitted_at DESC, scl.sku
    `, [locationId, programId, limit])

    const reportsMap = new Map()
    for (const row of rows) {
      if (!reportsMap.has(row.id)) {
        reportsMap.set(row.id, {
          id: row.stock_count_id,
          locationId: row.location_id,
          programId: row.program_id,
          yearweek: row.yearweek,
          submittedAt: row.submitted_at,
          products: {}
        })
      }
      if (!row.sku) continue

      // Use extra fields when exist or default to closing and sold columns
      const report = reportsMap.get(row.id)
      const extraFields = row.extra_fields || {}
      const soh = extraFields['field:standard-physical-count'] != null
        ? extraFields['field:standard-physical-count']
        : row.closing
      const consumed = extraFields['field:standard-consumed'] != null
        ? extraFields['field:standard-consumed']
        : row.sold

      report.products[addProductPrefix(row.sku)] = { soh, consumed }
    }

    return Array.from(reportsMap.values())
  }

  // Returns the most recent non-partial stock count at a location, or null if
  // none exist.
  async getLatestFullCount (locationFSId) {
    if (!locationFSId) throw new Error('locationFSId is required')

    const { rows } = await this.pgConnection.query(`
      SELECT id, stock_count_id, fs_id, program_id, yearweek, submitted_at
      FROM avocado.data_stockcount
      WHERE fs_id = $1 AND partial_count = false
      ORDER BY submitted_at DESC
      LIMIT 1
    `, [locationFSId])

    if (!rows.length) return null

    const row = rows[0]
    return {
      id: row.id,
      stockCountId: row.stock_count_id,
      locationFSId: row.fs_id,
      programId: row.program_id,
      yearweek: row.yearweek,
      submittedAt: row.submitted_at
    }
  }

  // Returns the per-sku, per-event sl_balance timeline for a location, starting
  // at `since` (inclusive). Combines stock count lines (as count events) with
  // shipments sent (negative) and shipments received (positive). The mirrors the existing
  // ledger calculation query.
  async getLedgerTimeline (locationFSId, since) {
    if (!locationFSId) throw new Error('locationFSId is required to get timeline')
    if (!since) throw new Error('date is required to get timeline')

    const { rows } = await this.pgConnection.query(`
      WITH
      count_events AS (
        SELECT sl.sku, sl.closing, sc.submitted_at AS event_date
        FROM avocado.data_stockcount sc
        JOIN avocado.data_stockcountline sl ON sl.stock_count_id = sc.id
        WHERE sc.fs_id = $1
          AND sc.submitted_at >= $2
      ),
      origin_snap AS (
        SELECT ds.shipment_id, MIN(dss.created_at) AS snap_at
        FROM avocado.data_shipment ds
        JOIN avocado.data_shipmentsnapshot dss
          ON dss.shipment_id = ds.shipment_id AND dss.status = 'sent'
        WHERE ds.origin_id = $1
          AND ds.status IN ('sent','arrived','received')
        GROUP BY ds.shipment_id
      ),
      shipments_sent AS (
        SELECT dsl.origin_sku AS sku, -dsl.amount AS closing,
               COALESCE(os.snap_at, ds.created_at) AS event_date
        FROM avocado.data_shipment ds
        JOIN avocado.data_shipmentline dsl ON dsl.shipment_id = ds.id
        LEFT JOIN origin_snap os ON os.shipment_id = ds.shipment_id
        WHERE ds.origin_id = $1
          AND ds.status IN ('sent','arrived','received')
          AND COALESCE(os.snap_at, ds.created_at) >= $2
      ),
      shipments_received AS (
        SELECT dsl.destination_sku AS sku, dsl.amount AS closing,
               ds.updated_at AS event_date
        FROM avocado.data_shipment ds
        JOIN avocado.data_shipmentline dsl ON dsl.shipment_id = ds.id
        WHERE ds.destination_id = $1
          AND ds.status = 'received'
          AND ds.updated_at >= $2
      ),
      combined AS (
        SELECT sku, closing, event_date, true  AS is_count FROM count_events
        UNION ALL SELECT sku, closing, event_date, false FROM shipments_sent
        UNION ALL SELECT sku, closing, event_date, false FROM shipments_received
      ),
      activity AS (
        SELECT sku, closing, event_date, is_count,
          CASE
            WHEN is_count THEN event_date
            ELSE MAX(CASE WHEN is_count THEN event_date END) OVER (
                   PARTITION BY sku
                   ORDER BY event_date, is_count
                   ROWS BETWEEN UNBOUNDED PRECEDING AND 1 PRECEDING
                 )
          END AS partition_window
        FROM combined
      )
      SELECT sku, event_date, closing, is_count,
             SUM(closing) OVER (PARTITION BY partition_window, sku ORDER BY event_date) AS sl_balance
      FROM activity
      ORDER BY sku, event_date
    `, [locationFSId, since])

    return rows
  }

  // Returns partial stock counts at a location, submitted after `since`,
  // ordered by submitted_at ASC.
  async listPartialCounts (locationFSId, since) {
    if (!locationFSId) throw new Error('locationFSId is required')
    if (!since) throw new Error('since is required')

    const { rows } = await this.pgConnection.query(`
      SELECT stock_count_id, submitted_at
      FROM avocado.data_stockcount
      WHERE partial_count = true
        AND fs_id = $1
        AND submitted_at > $2
      ORDER BY submitted_at
    `, [locationFSId, since])

    return rows.map(row => ({
      stockCountId: row.stock_count_id,
      submittedAt: row.submitted_at
    }))
  }
}

module.exports = ReportPgAdapter
